import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Card,
  CardBody,
  Button,
} from 'reactstrap';
import moment from 'moment';
import logo from '../../assets/images/Logo.png';
import { handleViewFile } from 'utility/utilityFunctions';

interface OrganizationDetailsModalProps {
  isOpen: boolean;
  toggle: () => void;
  data: any | null;
}

const splitLinks = (value: any): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value)
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
};

const DocumentCard = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: any;
  icon: string;
}) => {
  const links = splitLinks(value);
  return (
    <Col xs={12} md={6} lg={4}>
      <Card className="border-0 shadow-sm h-100 overflow-hidden">
        <CardBody className="py-3">
          <div className="d-flex align-items-start gap-2">
            <span className="avatar-xs bg-primary-subtle text-primary rounded d-flex align-items-center justify-content-center flex-shrink-0">
              <i className={`${icon} fs-16`} />
            </span>
            <div className="flex-grow-1">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-1 fw-semibold">{label}</h6>
                <span className="badge bg-light text-muted">
                  {links.length || 0}
                </span>
              </div>
              {links.length ? (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {links.map((l, idx) => (
                    <Button
                      key={idx}
                      size="sm"
                      color="primary"
                      outline
                      className="py-1"
                      onClick={() => handleViewFile(l, 'noopener,noreferrer')}
                    >
                      {links.length > 1 ? `File ${idx + 1}` : 'View'}
                    </Button>
                  ))}
                </div>
              ) : (
                <span className="badge bg-warning-subtle text-warning mt-2">
                  Not uploaded
                </span>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

const OrganizationDetailsModal = ({
  isOpen,
  toggle,
  data,
}: OrganizationDetailsModalProps) => {
  if (!data) return null;

  const organizationLogo = data.logo || logo;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} className="bg-light p-3">
        Organization Details
      </ModalHeader>
      <ModalBody className="overflow-hidden">
        <Card className="border-0 shadow-sm mb-3 overflow-hidden">
          <CardBody>
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded bg-light d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 64, height: 64, overflow: 'hidden' }}
              >
                <img
                  src={organizationLogo}
                  alt="organization logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div className="flex-grow-1">
                <h5 className="mb-1">{data.organization || '-'}</h5>
                <div className="text-muted small">
                  {data.city || '-'}, {data.state || '-'}
                </div>
                <div className="text-muted small">{data.address || '-'}</div>
              </div>
              <div className="text-end">
                <div className="badge bg-secondary-subtle text-secondary">
                  {data.createdAt
                    ? moment(new Date(data.createdAt)).format('DD MMM YYYY')
                    : '—'}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0 fw-semibold">Documents</h6>
          <span className="text-muted small">Open files in new tab</span>
        </div>
        <Row className="g-3">
          <DocumentCard
            label="Misaal Shareef"
            value={data.misaalShareef}
            icon="ri-file-text-line"
          />
          <DocumentCard
            label="Directives (Al Vazarat Us Saifiyah)"
            value={data.directivesAlVazaratUsSaifiyah}
            icon="ri-article-line"
          />
          <DocumentCard
            label="Mazar Property Map"
            value={data.mazaarPropertyMap}
            icon="ri-map-2-line"
          />
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default OrganizationDetailsModal;
